/**
 * Supabase client. Replaces the fetch wrapper in lib/api.js.
 *
 * ON THE ANON KEY BEING IN THE BUNDLE.
 * Vite inlines VITE_* variables at build time, so the URL and anon key ship
 * inside the JS a browser downloads. That is correct and expected: the anon key
 * is a public identifier, not a credential. It grants nothing on its own,
 * because every table has RLS enabled and every policy keys off auth.uid().
 *
 * What must never appear here, or anywhere under client/, is SERVICE_ROLE_KEY
 * or JWT_SECRET. The service role bypasses RLS entirely. Putting it in the
 * frontend hands every visitor full read/write on the database.
 */
import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set at build time. '
    + 'See deploy/.env.example.',
  );
}

/**
 * Where the session is kept, which differs by platform.
 *
 * In a browser, localStorage - the default, and left alone deliberately. The
 * Preferences plugin's own web fallback is also localStorage but under a
 * different key prefix, so switching the web build over would invalidate every
 * session already signed in at pulse.apisaranyu.in on the next deploy.
 *
 * In a native shell, Preferences: UserDefaults on iOS, SharedPreferences on
 * Android. WKWebView storage is evictable under disk pressure, and losing it
 * signs the user out at a moment nothing in the app caused - the kind of bug
 * that cannot be reproduced on demand. The native stores are not evictable.
 *
 * supabase-js accepts an async storage adapter, which is what makes this a
 * three-method object rather than a shim around a synchronous API.
 */
const nativeStorage = {
  getItem: async (key) => (await Preferences.get({ key })).value,
  setItem: async (key, value) => { await Preferences.set({ key, value }); },
  removeItem: async (key) => { await Preferences.remove({ key }); },
};

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,   // needed for invite and password-recovery links
    storageKey: 'pulse.auth',
    ...(Capacitor.isNativePlatform() ? { storage: nativeStorage } : {}),
  },
  realtime: {
    // Client-side cap on messages/second. A board-wide drag storm should slow
    // the sender down rather than knock the Realtime container over.
    params: { eventsPerSecond: 20 },
  },
});

/**
 * Private Realtime channels authorise against realtime.messages RLS, which
 * needs the caller's JWT. supabase-js does not push token refreshes into the
 * socket on its own, so a session that refreshes mid-connection would start
 * failing channel joins until reload without this.
 */
supabase.auth.onAuthStateChange((_event, session) => {
  supabase.realtime.setAuth(session?.access_token ?? null);
});

/** Unwraps the { data, error } envelope so callers can keep using try/catch. */
export function unwrap({ data, error }) {
  if (error) throw new Error(error.message);
  return data;
}
